import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Upload,
  FileText,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  CloudUpload,
  Shield,
  Clock,
  File,
  Mail,
  Info
} from 'lucide-react';
import api, { uploadFiles as apiUploadFiles } from '../utils/api';

// File size limits
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const RECOMMENDED_EMAIL_FILES = 5; // Show portal recommendation above this

// Document type icons
const FILE_ICONS: Record<string, React.ReactNode> = {
  'application/pdf': <FileText className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />,
  'text/csv': <FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />,
  'application/vnd.ms-excel': <FileSpreadsheet className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />,
};

interface MissingDoc {
  name: string;
  reason: string;
}

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  docType?: string;
  sizeError?: boolean;
}

interface ClaimInfo {
  claimant_name: string;
  business_type: string;
  event_type: string;
  status: string;
  missing_docs: MissingDoc[];
  inbound_email?: string;
}

export default function ClientUploadPortal() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  // Magic-link token from the email URL (?token=...). Required as of
  // 2026-04-19 PM — raw report_id alone no longer authorizes portal
  // access. The attorney + notifications flows both emit URLs shaped
  // ``/upload/{reportId}?token={portal_token}``.
  const token = searchParams.get('token') || '';
  const [claimInfo, setClaimInfo] = useState<ClaimInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  // Generate inbound email address - using updates.claimsync.io domain
  const inboundEmail = `docs+${reportId}@updates.claimsync.io`;

  useEffect(() => {
    if (!token) {
      setError('This upload link is missing its access token. Please use the original link from your email.');
      setLoading(false);
      return;
    }
    fetchClaimInfo();
  }, [reportId, token]);

  const fetchClaimInfo = async () => {
    try {
      setLoading(true);
      // Public endpoint - use centralized API utility
      const data = await api.get(
        `/api/v1/notifications/upload-portal/${reportId}?token=${encodeURIComponent(token)}`,
        { showErrorToast: false },
      );
      setClaimInfo(data);
    } catch (err: any) {
      if (err.message?.includes('401') || err.message?.includes('unauthorized')) {
        setError('This upload link is invalid or has expired.');
      } else if (err.message?.includes('404') || err.message?.includes('not found')) {
        setError('This upload link is invalid or has expired.');
      } else {
        setError(err.message || 'Failed to load claim information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validTypes = [
      'application/pdf',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const uploadFiles: UploadedFile[] = newFiles
      .filter(file => validTypes.includes(file.type) || file.name.endsWith('.csv'))
      .map(file => ({
        file,
        status: 'pending' as const,
        docType: guessDocType(file.name),
        sizeError: file.size > MAX_FILE_SIZE_BYTES
      }));

    // Track upload analytics
    trackUploadEvent('files_added', {
      count: uploadFiles.length,
      totalSize: uploadFiles.reduce((sum, f) => sum + f.file.size, 0),
      oversizedCount: uploadFiles.filter(f => f.sizeError).length
    });

    setFiles(prev => [...prev, ...uploadFiles]);
  };

  // Analytics tracking helper - fire and forget, no error handling needed
  const trackUploadEvent = async (event: string, data: Record<string, any>) => {
    try {
      await api.post('/api/v1/notifications/track-upload-analytics', {
        report_id: reportId,
        event,
        data,
        timestamp: new Date().toISOString()
      }, { showErrorToast: false, logError: false });
    } catch (err) {
      // Silent fail - analytics shouldn't break UX
      console.debug('Analytics tracking failed:', err);
    }
  };

  const guessDocType = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (lower.includes('1065') || lower.includes('tax') || lower.includes('return')) return 'Tax Returns';
    if (lower.includes('k-1') || lower.includes('k1')) return 'K-1 Forms';
    if (lower.includes('bank') || lower.includes('statement')) return 'Bank Statements';
    if (lower.includes('p&l') || lower.includes('pnl') || lower.includes('profit')) return 'P&L Statements';
    if (lower.includes('sales') || lower.includes('revenue')) return 'Sales Reports';
    if (lower.includes('payroll')) return 'Payroll Records';
    if (lower.includes('invoice')) return 'Invoices';
    return 'Financial Document';
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    
    // Filter out oversized files
    const validFiles = files.filter(f => !f.sizeError);
    if (validFiles.length === 0) {
      setError('All selected files exceed the size limit. Please reduce file sizes.');
      return;
    }

    setUploading(true);
    trackUploadEvent('upload_started', { fileCount: validFiles.length });
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      if (files[i].status !== 'pending' || files[i].sizeError) continue;
      
      // Update status to uploading
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' as const } : f
      ));
      
      try {
        const formData = new FormData();
        formData.append('file', files[i].file);
        formData.append('doc_type', files[i].docType || 'unknown');
        
        // Use centralized API utility for file upload
        await apiUploadFiles(
          `/api/v1/notifications/upload-document/${reportId}?token=${encodeURIComponent(token)}`,
          formData,
          { showErrorToast: false },
        );
        
        // Update status to success
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'success' as const } : f
        ));
        successCount++;
      } catch (err) {
        // Update status to error
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error' as const, error: 'Upload failed' } : f
        ));
        errorCount++;
      }
    }
    
    setUploading(false);
    
    // Track completion
    trackUploadEvent('upload_completed', { successCount, errorCount });
    
    // Check if all uploads succeeded
    if (successCount > 0 && errorCount === 0) {
      setUploadComplete(true);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
          <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>Loading upload portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="max-w-md w-full rounded-xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-needs-human-text)' }} />
          <h1 className="cs-display mb-2">Upload Link Invalid</h1>
          <p className="cs-body mb-6" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <p className="cs-meta">
            Please contact your account representative for a new upload link.
          </p>
        </div>
      </div>
    );
  }

  if (uploadComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="max-w-md w-full rounded-xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'transparent' }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: 'var(--color-verified)' }} />
          </div>
          <h1 className="cs-display mb-2">Upload Complete!</h1>
          <p className="cs-body mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            Thank you for uploading your documents. Our team will review them and update your claim analysis.
          </p>
          <p className="cs-meta">
            You will receive an email once your documents have been processed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }}>
      {/* Header */}
      <div className="py-8 px-4" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-3xl mx-auto">
          <h1 className="cs-display mb-2">The Figure Co.</h1>
          <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>Secure Document Upload Portal</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Claim Info Card */}
        {claimInfo && (
          <div className="rounded-xl p-6 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <h2 className="cs-h3 mb-4">
              Claim Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="cs-meta">Claimant</p>
                <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{claimInfo.claimant_name}</p>
              </div>
              <div>
                <p className="cs-meta">Business Type</p>
                <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{claimInfo.business_type}</p>
              </div>
              <div>
                <p className="cs-meta">Event Type</p>
                <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{claimInfo.event_type}</p>
              </div>
              <div>
                <p className="cs-meta">Status</p>
                <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{claimInfo.status}</p>
              </div>
            </div>
          </div>
        )}

        {/* Missing Documents */}
        {claimInfo?.missing_docs && claimInfo.missing_docs.length > 0 && (
          <div className="rounded-xl p-6 mb-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <h3 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
              <Clock className="w-5 h-5" />
              Documents Needed ({claimInfo.missing_docs.length})
            </h3>
            <div className="space-y-3">
              {claimInfo.missing_docs.map((doc, i) => (
                <div key={i} className="flex items-start gap-3 rounded-lg p-3" style={{ background: 'var(--color-bg-surface)' }}>
                  <div className="w-2 h-2 rounded-full mt-2" style={{ background: 'var(--color-brand)' }} />
                  <div>
                    <p className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>{doc.name}</p>
                    <p className="cs-meta">{doc.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRIMARY: Upload Area - Emphasized as main action */}
        <div className="rounded-xl p-6 mb-6" style={{ background: 'var(--color-bg-surface)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="cs-h3">
              Upload Your Documents
            </h3>
            <span className="cs-chip px-2 py-1 rounded-full" style={{ background: 'transparent', color: 'var(--color-verified)' }}>
              ✓ Recommended
            </span>
          </div>

          {/* File size guidance */}
          <div className="cs-body flex items-start gap-2 mb-4 p-3 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-text-secondary)' }}>
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} />
            <div>
              <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Best for multiple or large files</p>
              <p className="cs-meta">Files up to {MAX_FILE_SIZE_MB}MB each • PDF, CSV, Excel supported</p>
            </div>
          </div>

          {/* Drag & Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className="rounded-xl p-8 text-center transition-colors"
            style={
              isDragging
                ? { border: '2px dashed var(--color-brand)', background: 'var(--color-needs-human-tint)' }
                : { border: '2px dashed var(--color-border)' }
            }
          >
            <CloudUpload className="w-12 h-12 mx-auto mb-4" style={{ color: isDragging ? 'var(--color-brand)' : 'var(--color-text-dim)' }} />
            <p className="cs-body mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              Drag and drop your files here, or
            </p>
            <label className="cs-body inline-flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition font-medium" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
              <Upload className="w-5 h-5" />
              Browse Files
              <input
                type="file"
                multiple
                accept=".pdf,.csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              <h4 className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>Selected Files ({files.length})</h4>
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 rounded-lg"
                  style={
                    file.sizeError
                      ? { background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }
                      : { background: 'var(--color-bg-elevated)' }
                  }
                >
                  {FILE_ICONS[file.file.type] || <File className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />}
                  <div className="flex-1 min-w-0">
                    <p className="cs-body font-medium truncate" style={{ color: file.sizeError ? 'var(--color-needs-human-text)' : 'var(--color-text-primary)' }}>
                      {file.file.name}
                    </p>
                    <p className="cs-meta" style={{ color: file.sizeError ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)' }}>
                      {formatFileSize(file.file.size)} • {file.docType}
                      {file.sizeError && ` • Exceeds ${MAX_FILE_SIZE_MB}MB limit`}
                    </p>
                  </div>
                  {file.status === 'pending' && !file.sizeError && (
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1"
                      style={{ color: 'var(--color-text-dim)' }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {file.sizeError && (
                    <div className="flex items-center gap-1">
                      <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1"
                        style={{ color: 'var(--color-text-dim)' }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {file.status === 'uploading' && (
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-brand)' }} />
                  )}
                  {file.status === 'success' && (
                    <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  )}
                  {file.status === 'error' && !file.sizeError && (
                    <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  )}
                </div>
              ))}

              {/* Size error warning */}
              {files.some(f => f.sizeError) && (
                <div className="cs-body flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Some files exceed the {MAX_FILE_SIZE_MB}MB limit. Please reduce their size or split into smaller files.
                  </span>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={uploadFiles}
                disabled={uploading || files.every(f => f.status !== 'pending' || f.sizeError)}
                className="cs-body w-full mt-4 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <CloudUpload className="w-5 h-5" />
                    Upload All Documents
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* SECONDARY: Email Option - Fallback */}
        <div className="rounded-xl p-6 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            <h3 className="cs-body font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
              Alternative: Email Your Documents
            </h3>
          </div>
          <p className="cs-meta mb-4">
            For quick, single-file submissions only. For 5+ files or large documents,
            please use the secure upload portal above for faster processing.
          </p>
          <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <span className="cs-figure flex-1 truncate" style={{ color: 'var(--color-needs-human-text)' }}>
              {inboundEmail}
            </span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(inboundEmail);
                setEmailCopied(true);
                trackUploadEvent('email_copied', {});
                setTimeout(() => setEmailCopied(false), 2000);
              }}
              className="cs-meta px-3 py-1.5 rounded font-medium transition"
              style={{ background: 'var(--color-bg-hover)', color: 'var(--color-text-secondary)' }}
            >
              {emailCopied ? '✓ Copied!' : 'Copy'}
            </button>
            <a
              href={`mailto:${inboundEmail}?subject=Documents for Claim`}
              onClick={() => trackUploadEvent('email_opened', {})}
              className="cs-meta px-3 py-1.5 rounded font-medium transition"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Open Email
            </a>
          </div>
          <p className="cs-meta mt-2">
            Documents sent here will be automatically attached to your claim.
          </p>
        </div>

        {/* Security Notice */}
        <div className="cs-meta flex items-start gap-3">
          <Shield className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
          <p>
            Your documents are encrypted and transmitted securely.
            Only authorized personnel can access your files.
          </p>
        </div>
      </div>
    </div>
  );
}
